import {IDIOMAS_DE_EMPRESA, TIPOS_DE_EMPRESA, type Empresa, type TipoDeEmpresa} from '@cnc/contracts';
import {useState, type ReactNode} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useGuardarEmpresa} from '../api/empresas';
import {tr, type Texto} from '../i18n/idioma';
import {AYUDA_EMPRESAS as A} from './ayuda-general';
import {NotaDeAyuda} from './nota-de-ayuda';
import {nombreDeTipo} from './tipos-de-empresa';

const NOMBRE_DE_IDIOMA: Record<string, [string, string]> = {
  es: ['Español', 'Spanish'],
  en: ['Inglés', 'English'],
  hi: ['Hindi', 'Hindi'],
  pt: ['Portugués', 'Portuguese'],
  fr: ['Francés', 'French'],
  de: ['Alemán', 'German'],
  it: ['Italiano', 'Italian'],
  zh: ['Chino', 'Chinese'],
  ar: ['Árabe', 'Arabic'],
};

type Campos = {
  nombre: string;
  abreviacion: string;
  identificacion: string;
  direccion: string;
  direccionDeEntrega1: string;
  direccionDeEntrega2: string;
  direccionDeEntrega3: string;
  idioma: string;
  contactoNombre: string;
  contactoTelefono: string;
  contactoCorreo: string;
  notas: string;
};
const VACIA: Campos = {
  nombre: '',
  abreviacion: '',
  identificacion: '',
  direccion: '',
  direccionDeEntrega1: '',
  direccionDeEntrega2: '',
  direccionDeEntrega3: '',
  idioma: '',
  contactoNombre: '',
  contactoTelefono: '',
  contactoCorreo: '',
  notas: '',
};

// Ficha de una empresa (Daniel, 15/10/2026): crearla en la app o cambiar sus
// datos. Sin empresa, es una nueva.
export function FichaDeEmpresa({
  empresa,
  puedeEditar,
  onCerrar,
  onGuardada,
}: {
  empresa: Empresa | null;
  puedeEditar: boolean;
  onCerrar: () => void;
  onGuardada: (e: Empresa, creada: boolean) => void;
}) {
  const guardar = useGuardarEmpresa();
  const [campos, setCampos] = useState<Campos>(() =>
    empresa
      ? (Object.fromEntries(
          Object.keys(VACIA).map(k => [k, (empresa as unknown as Record<string, string | null>)[k] ?? '']),
        ) as Campos)
      : VACIA,
  );
  const porDatos = new Set(empresa?.rolesPorDatos ?? []);
  const [tipos, setTipos] = useState<Set<string>>(() => new Set([...(empresa?.roles ?? []), ...porDatos]));
  const [activa, setActiva] = useState(empresa?.activa ?? true);
  const [error, setError] = useState<string | null>(null);
  const poner = (k: keyof Campos) => (v: string) => setCampos(c => ({...c, [k]: v}));
  // Los seis tipos de siempre; los demas solo si la empresa ya los tiene o se piden.
  const [todos, setTodos] = useState(false);
  const visibles = TIPOS_DE_EMPRESA.filter((t, i) => i < 6 || todos || tipos.has(t));

  const enviar = async () => {
    setError(null);
    const datos = {...campos, roles: [...tipos] as TipoDeEmpresa[], activa};
    try {
      const r = await guardar.mutateAsync({
        id: empresa?.id ?? null,
        datos: empresa ? {...datos, version: empresa.version} : datos,
      });
      onGuardada(r.empresa, r.creada);
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  const texto = (k: keyof Campos, etiqueta: string, ayuda: Texto, opciones: {ancho?: string; tipo?: string} = {}) => (
    <Campo etiqueta={etiqueta} ayuda={ayuda} ancho={opciones.ancho}>
      <input
        type={opciones.tipo ?? 'text'}
        value={campos[k]}
        onChange={e => poner(k)(e.target.value)}
        className="mt-0.5 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta"
      />
    </Campo>
  );

  return (
    <form
      className="space-y-3 rounded-lg border border-verde/40 bg-white p-4"
      aria-label={
        empresa ? tr(`Ficha de ${empresa.nombre}`, `Record of ${empresa.nombre}`) : tr('Nueva empresa', 'New company')
      }
      data-prueba="ficha-de-empresa"
      onSubmit={e => {
        e.preventDefault();
        if (campos.nombre.trim()) void enviar();
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-lg font-semibold text-verde-oscuro">
          {empresa ? empresa.nombre : tr('Nueva empresa', 'New company')}
        </h2>
        <button type="button" onClick={onCerrar} aria-label={tr('Cerrar', 'Close')} className="text-apagado">
          ×
        </button>
      </div>
      <fieldset disabled={!puedeEditar || guardar.isPending} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
          {texto('nombre', tr('Nombre de la empresa', 'Company name'), A.nombre)}
          {texto('abreviacion', tr('Abreviación', 'Abbreviation'), A.abreviacion)}
          {texto('identificacion', tr('ID (NIT, Tax ID)', 'ID (NIT, Tax ID)'), A.identificacion)}
        </div>
        <div>
          <div className="text-xs text-apagado">
            {tr('Tipo (puede ser varios)', 'Type (it can be several)')}
            <NotaDeAyuda texto={A.tipos} />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {visibles.map(t => (
              <label key={t} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={tipos.has(t)}
                  disabled={porDatos.has(t)}
                  onChange={e =>
                    setTipos(s => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(t);
                      else n.delete(t);
                      return n;
                    })
                  }
                />
                {nombreDeTipo(t)}
                {porDatos.has(t) && (
                  <span className="text-xs text-apagado">{tr('(por los datos)', '(from the data)')}</span>
                )}
              </label>
            ))}
            {!todos && visibles.length < TIPOS_DE_EMPRESA.length && (
              <button type="button" onClick={() => setTodos(true)} className="text-xs text-verde hover:underline">
                {tr('Más tipos…', 'More types…')}
              </button>
            )}
          </div>
        </div>
        {texto('direccion', tr('Dirección', 'Address'), A.direccion)}
        <div className="grid gap-3 sm:grid-cols-3">
          {texto('direccionDeEntrega1', tr('Dirección de entrega 1', 'Delivery address 1'), A.entrega)}
          {texto('direccionDeEntrega2', tr('Dirección de entrega 2', 'Delivery address 2'), A.entrega)}
          {texto('direccionDeEntrega3', tr('Dirección de entrega 3', 'Delivery address 3'), A.entrega)}
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Campo etiqueta={tr('Idioma', 'Language')} ayuda={A.idioma}>
            <select
              value={campos.idioma}
              onChange={e => poner('idioma')(e.target.value)}
              className="mt-0.5 block w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
            >
              <option value="">—</option>
              {IDIOMAS_DE_EMPRESA.map(i => (
                <option key={i} value={i}>
                  {tr(NOMBRE_DE_IDIOMA[i]![0], NOMBRE_DE_IDIOMA[i]![1])}
                </option>
              ))}
              {campos.idioma && !(IDIOMAS_DE_EMPRESA as readonly string[]).includes(campos.idioma) && (
                <option value={campos.idioma}>{campos.idioma}</option>
              )}
            </select>
          </Campo>
          {texto('contactoNombre', tr('Nombre de contacto', 'Contact name'), A.contactoNombre)}
          {texto('contactoTelefono', tr('Teléfono', 'Phone'), A.contactoTelefono, {tipo: 'tel'})}
          {texto('contactoCorreo', tr('Correo', 'Email'), A.contactoCorreo, {tipo: 'email'})}
        </div>
        <Campo etiqueta={tr('Notas', 'Notes')} ayuda={A.notas}>
          <textarea
            value={campos.notas}
            onChange={e => poner('notas')(e.target.value)}
            rows={2}
            className="mt-0.5 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta"
          />
        </Campo>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={activa} onChange={e => setActiva(e.target.checked)} />
          {tr('Activa', 'Active')}
          <NotaDeAyuda texto={A.activa} dentroDeEtiqueta />
        </label>
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-alerta">
          {error}
        </p>
      )}
      {puedeEditar && (
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!campos.nombre.trim() || guardar.isPending}
            className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {empresa ? tr('Guardar cambios', 'Save changes') : tr('Crear empresa', 'Create company')}
          </button>
          <button
            type="button"
            onClick={onCerrar}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
          >
            {tr('Cancelar', 'Cancel')}
          </button>
        </div>
      )}
    </form>
  );
}

function Campo({
  etiqueta,
  ayuda,
  ancho,
  children,
}: {
  etiqueta: string;
  ayuda: Texto;
  ancho?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block text-xs text-apagado ${ancho ?? ''}`}>
      {etiqueta}
      <NotaDeAyuda texto={ayuda} dentroDeEtiqueta />
      {children}
    </label>
  );
}
